import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { useAuth } from '../../shared/useAuth.js';
import { UsersTable } from './components/users/UsersTable.jsx';
import { RolesPanel } from './components/roles/RolesPanel.jsx';
import { DevicesTable } from './components/devices/DevicesTable.jsx';
import { AuditLogTable } from './components/audit/AuditLogTable.jsx';
import { SystemTab } from './components/system/SystemTab.jsx';
import { GeneralTab } from './components/general/GeneralTab.jsx';

const TABS = [
  { key: 'users', label: 'المستخدمون', permission: 'settings.users.view' },
  { key: 'roles', label: 'الأدوار والصلاحيات', permission: 'settings.roles.view' },
  { key: 'general', label: 'الإعدادات العامة', permission: 'settings.general.manage' },
  { key: 'devices', label: 'الأجهزة', permission: 'settings.devices.manage' },
  { key: 'audit', label: 'سجل التدقيق', permission: 'settings.audit.view' },
  { key: 'system', label: 'إدارة النظام', permission: 'system.manage' }
];

function Tabs({ value, onChange, available }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 bg-white border border-line rounded-[10px] shadow-card">
      {available.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={clsx(
            'px-3.5 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
            value === t.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-paper-2'
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function SettingsScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const [params, setParams] = useSearchParams();

  const available = useMemo(
    () => TABS.filter((t) => hasPermission(t.permission)),
    [hasPermission]
  );

  const requested = params.get('tab');
  const tab = useMemo(() => {
    if (requested && available.some((t) => t.key === requested)) return requested;
    return available[0]?.key || null;
  }, [requested, available]);

  useEffect(() => {
    if (tab && tab !== requested) {
      const next = new URLSearchParams(params);
      next.set('tab', tab);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function setTab(next) {
    const np = new URLSearchParams(params);
    np.set('tab', next);
    setParams(np);
  }

  if (!available.length) {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="text-xl font-extrabold tracking-tight m-0">الإعدادات</h2>
        <Card>
          <div className="text-ink-3 text-center py-12 text-sm">
            لا تملك صلاحيات لهذا القسم.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">الإعدادات</h2>
          <div className="text-ink-3 text-xs mt-0.5">
            المستخدمون والأدوار والأجهزة وسجل التدقيق
          </div>
        </div>
        <Tabs value={tab} onChange={setTab} available={available} />
      </div>

      {tab === 'users' ? <UsersTable /> : null}
      {tab === 'roles' ? <RolesPanel /> : null}
      {tab === 'general' ? <GeneralTab /> : null}
      {tab === 'devices' ? <DevicesTable /> : null}
      {tab === 'audit' ? <AuditLogTable /> : null}
      {tab === 'system' ? <SystemTab /> : null}
    </div>
  );
}
